
import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { FiClock } from "react-icons/fi";
import * as apicall from '../../components/Apicall';
import { FaCalendarAlt } from 'react-icons/fa';
import { toast } from 'react-toastify';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { parseISO, format } from 'date-fns';

const AppointmentModal = ({ show, onHide, reqData, info, holidayDates = [] }) => {
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTimes, setSelectedTimes] = useState([]);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [showTimeDropdown, setShowTimeDropdown] = useState(false);
  const [isUserSelecting, setIsUserSelecting] = useState(false);

  const timeSlotRef = useRef(null);

  useEffect(() => {
    if (show) {
      setIsUserSelecting(false);
      setSelectedDate(reqData?.appointmentDate ? parseISO(reqData.appointmentDate) : null);
      setSelectedTimes(reqData?.appointmentTime ? [reqData.appointmentTime] : []);
    }
  }, [show, reqData]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (timeSlotRef.current && !timeSlotRef.current.contains(event.target)) {
        setShowTimeDropdown(false);
      }
    }
  
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleDateChange = (date) => {
    setSelectedDate(date);
    setSelectedTimes([]); // Clear selected time slots when date changes
    setShowTimeDropdown(false); // Close time dropdown if open
  };

  useEffect(() => {
    if (selectedDate && info?.doctor_id) {
      setIsUserSelecting(false);
      const formattedDate = format(selectedDate, 'yyyy-MM-dd');
      fetchDoctorAvailability(info.doctor_id, formattedDate);
    }
  }, [selectedDate, info?.doctor_id]);

  const fetchDoctorAvailability = async (doctorId, selectedDateStr) => {
    try {
      const response = await apicall.get(`get_available_slots/${doctorId}/${selectedDateStr}`);
      if (response && response.data) {
        setAvailableSlots(response.data.available_slots || []);
      } else {
        setAvailableSlots([]);
      }
    } catch (error) {
      console.error('Error fetching doctor availability:', error);
      toast.error('Failed to fetch available slots');
      setAvailableSlots([]);
    }
  };

  const handleTimeSlotClick = (slot) => {
    if (!isUserSelecting) {
      setIsUserSelecting(true);
      setSelectedTimes([slot]);
    } else {
      setSelectedTimes((prev) =>
        prev.includes(slot) ? prev.filter((s) => s !== slot) : [...prev, slot]
      );
    }
  };

  const convertTimeValueTo24 = (slot) => {
    const startTime12h = slot.split('-')[0].trim();
    const [time, modifier] = startTime12h.split(' ');
    let [hours, minutes] = time.split(':').map(Number);
    if (modifier === 'PM' && hours !== 12) hours += 12;
    if (modifier === 'AM' && hours === 12) hours = 0;
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  };

  const applyAction = async () => {
    if (!selectedTimes.length) {
      toast.error('Please select a time slot');
      return;
    }

    const req_body = {
      doctor_id: info?.doctor_id,
      user_id: info?.user_id,
      appointment_date: format(selectedDate, 'yyyy-MM-dd'),
      time_slots: selectedTimes.map(convertTimeValueTo24),
      vendor_id: localStorage.getItem('vendorId'),
      service_id: info?.service_id,
      service_offering: info?.service_offering,
      appointment_type: info?.user_id !== 0 ? "self" : "dependent",
      insurance_provider: info?.insurance_provider,
      insurance_number: info?.insurance_number,
      ...(info?.dependent_id !== 0 && { dependent_id: info?.dependent_id }),
    };

    try {
      const response = await apicall.post(req_body, 'book-appointment');
      if (response.status === 200) {
        toast.success('Follow-up Appointment booked successfully');
        onHide();
      } else {
        console.log("response", response.message);
      }
    } catch (error) {
      console.error('Error creating availability:', error);
      toast.error(error?.message || 'Failed to book appointment');
    }
  };

  const timeSlotStyles = {
    dropdownContainer: { position: 'relative' },
    timeSlotList: {
      position: 'absolute',
      top: '100%',
      left: 0,
      right: 0,
      backgroundColor: 'white',
      borderRadius: '8px',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
      maxHeight: '300px',
      overflowY: 'auto',
      zIndex: 1000,
      padding: '8px',
      marginTop: '4px',
      border: '1px solid #E3EBF8'
    },
    timeSlotItem: {
      backgroundColor: '#F0F7FF',
      borderRadius: '6px',
      padding: '8px 12px',
      margin: '4px 0',
      cursor: 'pointer',
      fontSize: '12px',
      border: '1px solid #E3EBF8',
      width: '100%',
      textAlign: 'left',
      transition: 'all 0.2s ease',
      color: '#212529'
    },
    timeSlotItemSelected: {
      backgroundColor: '#E3F2FF',
      border: '1px solid #0D6EFD',
      color: '#0D6EFD'
    },
    timeInput: {
      height: '40px',
      backgroundColor: 'white',
      border: '1px solid #E3EBF8',
      borderRadius: '8px',
      padding: '8px 16px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      fontSize: '14px',
      color: '#212529'
    }
  };

  return (
    <>
      <style>{`
        .custom-appointment-modal {
          max-width: 40% !important;
        }
        .custom-appointment-modal .modal-content {
          width: 100%;
        }
      `}</style>

      <Modal
        show={show}
        onHide={onHide}
        centered
        backdrop="static"
        dialogClassName="custom-appointment-modal"
        contentClassName="rounded-4 border-0"
      >
        <Modal.Header closeButton className="border-0 pt-4 px-4">
          <Modal.Title className="w-100 text-center fs-5 fw-bold">
            Follow Up Appointment
          </Modal.Title>
        </Modal.Header>

        <hr className="mx-4 my-0" />

        <Modal.Body className="px-4 py-4">
          <div className="mb-4">
            <h6 className="fw-bold mb-3 text-center">Select Next Appointment Date</h6>
            <div className="d-flex flex-row align-items-center gap-3">
              <div className="position-relative w-50">
                <DatePicker
                  selected={selectedDate}
                  onChange={handleDateChange}  // Updated to use handleDateChange
                  minDate={new Date()}
                  excludeDates={holidayDates?.map((dateStr) => {
                    const [year, month, day] = dateStr.split("-");
                    return new Date(year, month - 1, day);
                  }) || []}
                  filterDate={(date) => {
                    const year = date.getFullYear();
                    const month = String(date.getMonth() + 1).padStart(2, "0");
                    const day = String(date.getDate()).padStart(2, "0");
                    const dateString = `${year}-${month}-${day}`;
                    return !holidayDates?.includes(dateString);
                  }}
                  dayClassName={(date) => {
                    const year = date.getFullYear();
                    const month = String(date.getMonth() + 1).padStart(2, "0");
                    const day = String(date.getDate()).padStart(2, "0");
                    const dateString = `${year}-${month}-${day}`;
                    return holidayDates?.includes(dateString) ? "disabled-day" : null;
                  }}
                  dateFormat="yyyy-MM-dd"
                  customInput={
                    <div className="d-flex align-items-center position-relative">
                      <input
                        className="form-control pe-5"
                        value={selectedDate ? format(selectedDate, 'yyyy-MM-dd') : ''}
                        readOnly
                        placeholder="Select date"
                      />
                      <FaCalendarAlt
                        className="position-absolute end-0 me-3 text-muted"
                        style={{ pointerEvents: 'none' }}
                      />
                    </div>
                  }
                />
              </div>

              <div className="w-50 position-relative" ref={timeSlotRef}>
                <div
                  style={timeSlotStyles.timeInput}
                  onClick={() => selectedDate && setShowTimeDropdown(!showTimeDropdown)}
                  className={!selectedDate ? 'opacity-50' : ''}
                >
                  <span>
                    {selectedTimes.length > 0
                      ? selectedTimes.map(t => t.split('-')[0].trim()).join(', ')
                      : 'Select Time'}
                  </span>
                  <FiClock className="text-muted" />
                </div>

                {showTimeDropdown && selectedDate && (
                  <div style={timeSlotStyles.timeSlotList}>
                    {availableSlots.length > 0 ? (
                      availableSlots.map((slot, index) => (
                        <div
                          key={index}
                          onClick={() => handleTimeSlotClick(slot)}
                          style={{
                            ...timeSlotStyles.timeSlotItem,
                            ...(selectedTimes.includes(slot) && timeSlotStyles.timeSlotItemSelected)
                          }}
                        >
                          {slot.split('-')[0].trim()}
                        </div>
                      ))
                    ) : (
                      <div style={{ padding: '8px', textAlign: 'center', color: '#6c757d' }}>
                        No available slots
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </Modal.Body>

        <hr className="mx-4 my-0" />

        <Modal.Footer className="border-0 d-flex justify-content-center gap-3 py-3">
          <Button 
            onClick={applyAction} 
            variant="primary" 
            className="px-4 rounded"
            disabled={!selectedDate || !selectedTimes.length}
          >
            Submit
          </Button>
          <Button onClick={onHide} variant="outline-secondary" className="px-4 rounded">Cancel</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default AppointmentModal;


